import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  loadOrInitVisitor,
  updateScrollDepth,
  addTimeOnSite,
  markConverted as markConvertedInStorage,
  computeTier,
  fetchGeo,
  bucket,
  trackEvent,
} from "@/lib/personalization";
import type {
  ExperimentDef,
  GeoInfo,
  VisitorState,
  VisitorTier,
} from "@/lib/personalization";

interface PersonalizationCtx {
  visitor: VisitorState;
  geo: GeoInfo | null;
  tier: VisitorTier;
  getVariant: <V extends string>(def: ExperimentDef<V>) => V;
  markConverted: () => void;
}

const PersonalizationContext = createContext<PersonalizationCtx | null>(null);

export function PersonalizationProvider({ children }: { children: ReactNode }) {
  const [visitor, setVisitor] = useState<VisitorState>(() => loadOrInitVisitor());
  const [geo, setGeo] = useState<GeoInfo | null>(null);

  // Geo fetch (once, cached in sessionStorage downstream).
  useEffect(() => {
    let cancelled = false;
    fetchGeo()
      .then((g) => {
        if (!cancelled) setGeo(g);
      })
      .catch(() => {
        if (!cancelled) setGeo(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Scroll-depth tracking.
  useEffect(() => {
    const onScroll = () => {
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      if (docH <= 0) return;
      const depth = Math.min(1, Math.max(0, window.scrollY / docH));
      updateScrollDepth(depth);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Time-on-site (flush on unmount + beforeunload).
  useEffect(() => {
    const start = Date.now();
    const flush = () => {
      const elapsed = Date.now() - start;
      if (elapsed > 0 && elapsed < 30 * 60 * 1000) addTimeOnSite(elapsed);
    };
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      flush();
    };
  }, []);

  const tier = useMemo<VisitorTier>(() => computeTier(visitor), [visitor]);

  const getVariant = useCallback(
    <V extends string>(def: ExperimentDef<V>): V => bucket(def, visitor.id),
    [visitor.id],
  );

  const markConverted = useCallback(() => {
    markConvertedInStorage();
    setVisitor((v) => ({ ...v, converted: true }));
    trackEvent("visitor_converted", { tier });
  }, [tier]);

  const value = useMemo<PersonalizationCtx>(
    () => ({ visitor, geo, tier, getVariant, markConverted }),
    [visitor, geo, tier, getVariant, markConverted],
  );

  return (
    <PersonalizationContext.Provider value={value}>
      {children}
    </PersonalizationContext.Provider>
  );
}

export function usePersonalization(): PersonalizationCtx {
  const ctx = useContext(PersonalizationContext);
  if (!ctx) throw new Error("usePersonalization must be used within PersonalizationProvider");
  return ctx;
}

// Exposes a deterministic variant and fires an exposure event once per (name+variant).
export function useExperiment<V extends string>(def: ExperimentDef<V>): V {
  const { getVariant } = usePersonalization();
  const variant = getVariant(def);
  useEffect(() => {
    trackEvent("experiment_exposure", { experiment: def.name, variant });
  }, [def.name, variant]);
  return variant;
}
